import React, { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import showToast from '../../components/Toast';
import { FiUser, FiMail, FiBriefcase, FiLock, FiCamera, FiSave, FiEye, FiEyeOff, FiRefreshCw, FiList } from 'react-icons/fi';
import './Settings.css';
import AxiosInstance from '../../AxiosInstance';
import config from '../../config/frontend.config';

const profileValidationSchema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  companyName: Yup.string(),
  email: Yup.string().email('Invalid email address').required('Email is required'),
});

const passwordValidationSchema = Yup.object({
    currentPassword: Yup.string().required('Current password is required'),
    newPassword: Yup.string().required('New password is required').min(6, 'Password must be at least 6 characters'),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
        .required('Please confirm your new password'),
});

const passwordSetupValidationSchema = Yup.object({
    password: Yup.string()
        .required('Password is required')
        .min(8, 'Password must be at least 8 characters long')
        .matches(/(?=.*[a-z])/, 'Password must contain at least one lowercase letter')
        .matches(/(?=.*[A-Z])/, 'Password must contain at least one uppercase letter')
        .matches(/(?=.*\d)/, 'Password must contain at least one number')
        .matches(/(?=.*[@$!%*?&])/, 'Password must contain at least one special character (@$!%*?&)'),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
        .required('Please confirm your password'),
});

function Settings() {
  const baseUrl = config.api.baseURL + '/' + config.api.apiPath;
  
  // Get user data using config helper
  const user = config.getUserData();
  const isAuthenticated = !!localStorage.getItem('Token');
  
  const [activeTab, setActiveTab] = useState('profile');
  const [userData, setUserData] = useState(user);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch fresh user data from API
  const fetchUserData = async () => {
    if (!user?.UserId) {
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await AxiosInstance.get(`${baseUrl}/user/userdata?UserId=${user.UserId}`);
      
      
      if (response?.data?.statusCode === 200) {
        const freshUserData = response.data.data;
        
        // Preserve Password field in state for password check
        // (even though it won't be stored in localStorage for security)
        setUserData(freshUserData);
        
        // Update localStorage with fresh data (Password will be filtered out for security)
        config.setUserData(freshUserData);
        
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('userDataRefreshed', { 
          detail: freshUserData 
        }));
      } else {
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      // Fallback to localStorage data if API fails
      setUserData(user);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'security' || tab === 'profile') {
      setActiveTab(tab);
    } else {
      setActiveTab('profile');
    }
  }, [location]);

  // Fetch user data when component mounts and user is available
  useEffect(() => {
    if (user?.UserId) {
      fetchUserData();
    }
  }, [user?.UserId]);

  const handleProfileUpdate = async (values, { setSubmitting }) => {
    try {
      // Create FormData to handle both text data and file upload
      const formData = new FormData();
      formData.append('FirstName', values.firstName);
      formData.append('LastName', values.lastName);
      formData.append('CompanyName', values.companyName);
      formData.append('EmailAddress', values.email);

      // Add profile picture if selected
      if (values.profilePicture && values.profilePicture instanceof File) {
        formData.append('profilePicture', values.profilePicture);
      }

      const response = await AxiosInstance.put(
        `${baseUrl}/user/updateuser?UserId=${userData.UserId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response?.data?.statusCode === 200) {
        showToast.success(response.data.message || 'Profile updated successfully!');
        
        // Update user data in localStorage
        const updatedUserData = JSON.parse(localStorage.getItem('UserData') || '{}');
        updatedUserData.FirstName = values.firstName;
        updatedUserData.LastName = values.lastName;
        updatedUserData.CompanyName = values.companyName;
        updatedUserData.EmailAddress = values.email;
        
        // Update profile picture URL if it was uploaded
        if (response.data.data?.profilePicture) {
          updatedUserData.profilePicture = response.data.data.profilePicture;
        }
        
        localStorage.setItem('UserData', JSON.stringify(updatedUserData));
        
        // Update local state
        setUserData(updatedUserData);
        
        // Trigger user data refresh
        window.dispatchEvent(new CustomEvent('userDataRefreshed', { 
          detail: updatedUserData 
        }));
      } else {
        showToast.error(response.data?.message || 'Failed to update profile.');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      showToast.error('Failed to update profile. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handlePasswordChange = async (values, { setSubmitting, resetForm }) => {
    try {
      const response = await AxiosInstance.put(
        `${baseUrl}/resetpassword/changepassword?UserId=${userData.UserId}`,
        {
          oldPassword: values.currentPassword,
          newPassword: values.newPassword,
          confirmPassword: values.confirmPassword,
        }
      );
  
      if (response?.data?.statusCode === 200) {
        showToast.success(response.data.message || 'Password changed successfully!');
        resetForm();
      } else {
        showToast.error(response.data?.message || 'Failed to change password.');
      }
    } catch (error) {
      console.error('Password change error:', error);
      // Toast notification is handled by AxiosInstance interceptor
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordSetup = async (values, { setSubmitting, resetForm }) => {
    try {
      const response = await AxiosInstance.put(`${baseUrl}/user/update-password`, {
        UserId: userData.UserId,
        Password: values.password,
        ConfirmPassword: values.confirmPassword
      });

      if (response.data.statusCode === 200) {
        showToast.success('Password set successfully!');
        
        // Update user data - password is now set
        const updatedUserData = { ...userData, hasPassword: true };
        config.setUserData(updatedUserData);
        setUserData(updatedUserData);
        
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('userDataRefreshed', { 
          detail: updatedUserData 
        }));
        
        // Refresh user data from API to get latest state
        await fetchUserData();
        
        resetForm();
      } else {
        showToast.error(response.data.message || 'Failed to set password');
      }
    } catch (error) {
      console.error('Password setup error:', error);
      if (error.response?.data?.message) {
        showToast.error(error.response.data.message);
      } else {
        showToast.error('Failed to set password. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Check if user has password set
  // Use hasPassword flag from API if available, otherwise check Password field
  const hasPassword = userData?.hasPassword !== undefined 
                      ? userData.hasPassword 
                      : (userData?.Password !== null && 
                         userData?.Password !== undefined && 
                         userData?.Password !== '');

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileForm user={userData} handleSubmit={handleProfileUpdate} loading={loading} onRefresh={fetchUserData} />;
      case 'security':
        return hasPassword ? 
          <SecurityForm handleSubmit={handlePasswordChange}/> : 
          <SetPasswordForm handleSubmit={handlePasswordSetup}/>;
      default:
        return <ProfileForm user={userData} handleSubmit={handleProfileUpdate} loading={loading} onRefresh={fetchUserData} />;
    }
  };

  if (!userData) {
    // This prevents rendering with a null user, which causes the crash
    return null; 
  }

  return (
    <div className="settings-page">
      <div className="settings-container">
        <aside className="settings-sidebar">
            <h2 className="settings-main-title">Settings</h2>
            <nav className="settings-nav">
                <Link
                    to="/settings?tab=profile"
                    className={`nav-link ${activeTab === 'profile' ? 'active' : ''}`}
                >
                    <FiUser /> Profile
                </Link>
                <Link
                    to="/settings?tab=security"
                    className={`nav-link ${activeTab === 'security' ? 'active' : ''}`}
                >
                    <FiLock /> {hasPassword ? 'Change Password' : 'Set Password'}
                </Link>
            </nav>
        </aside>
        <main className="settings-content">
          {renderContent()}
        </main>
      </div>

    </div>
  );
}

const ProfileForm = ({ user, handleSubmit, loading, onRefresh }) => {
    const [profilePicture, setProfilePicture] = React.useState(user?.profilePicture || '');
    const [selectedFile, setSelectedFile] = React.useState(null);
    const [isUploading, setIsUploading] = React.useState(false);
    const fileInputRef = React.useRef(null);

    // Debug logging
    React.useEffect(() => {
    }, [user, profilePicture]);

    // Update profile picture when user data changes
    React.useEffect(() => {
      if (user?.profilePicture) {
        setProfilePicture(user.profilePicture);
      } else {
        setProfilePicture('');
      }
    }, [user?.profilePicture]);

    const handleAvatarClick = () => {
        fileInputRef.current?.click();
    };

    const handleProfilePictureUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Please select an image file');
            return;
        }

        // Validate file size (5MB limit)
        if (file.size > 5 * 1024 * 1024) {
            alert('File size must be less than 5MB');
            return;
        }

        // Store the selected file for form submission
        setSelectedFile(file);
        
        // Create a preview URL for immediate display
        const previewUrl = URL.createObjectURL(file);
        setProfilePicture(previewUrl);
    };

    return (
        <div className="settings-section">
            <h3 className="section-title">Public Profile</h3>
            <p className="section-subtitle">Update your personal information here.</p>
            <div className="profile-header">
                <div className="profile-avatar-large" onClick={handleAvatarClick}>
                    {profilePicture ? (
                        <img 
                            src={profilePicture} 
                            alt="Profile" 
                            className="profile-picture"
                            onError={(e) => {
                                setProfilePicture('');
                            }}
                        />
                    ) : (
                        <span className="fallback-avatar">
                            {user?.FirstName?.charAt(0) || ''}{user?.LastName?.charAt(0) || ''}
                        </span>
                    )}
                    <button 
                        className="avatar-upload-btn" 
                        disabled={isUploading || loading}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleAvatarClick();
                        }}
                    >
                        {isUploading ? <FiRefreshCw className="spinning" /> : <FiCamera />}
                    </button>
                </div>
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleProfilePictureUpload}
                    accept="image/*"
                    style={{ display: 'none' }}
                />
                <div className="profile-info">
                    <h4>{user?.FirstName || ''} {user?.LastName || ''}</h4>
                    <p>Click the camera icon to upload a new profile picture</p>
                    {loading && <p className="loading-text">Loading profile data...</p>}
                    <p>Debug: Profile picture URL: {profilePicture || 'None'}</p>
                </div>
            </div>

        <Formik
            initialValues={{
              firstName: user?.FirstName || '',
              lastName: user?.LastName || '',
              companyName: user?.CompanyName || '',
              email: user?.EmailAddress || '',
              profilePicture: selectedFile,
            }}
            validationSchema={profileValidationSchema}
            onSubmit={(values, formikBag) => {
              // Include the selected file in the values
              values.profilePicture = selectedFile;
              handleSubmit(values, formikBag);
            }}
            enableReinitialize
        >
            {({ isSubmitting, dirty }) => (
                <Form>
                    <div className="form-grid">
                        <div className="form-group">
                            <label htmlFor="firstName">First Name</label>
                             <div className="input-wrapper">
                                <FiUser className="input-icon" />
                                <Field type="text" name="firstName" id="firstName" placeholder="Your first name" />
                            </div>
                            <ErrorMessage name="firstName" component="div" className="field-error" />
                        </div>
                        <div className="form-group">
                            <label htmlFor="lastName">Last Name</label>
                            <div className="input-wrapper">
                                <FiUser className="input-icon" />
                                <Field type="text" name="lastName" id="lastName" placeholder="Your last name" />
                            </div>
                            <ErrorMessage name="lastName" component="div" className="field-error" />
                        </div>
                    </div>
                    <div className="form-group">
                        <label htmlFor="companyName">Company</label>
                        <div className="input-wrapper">
                            <FiBriefcase className="input-icon" />
                            <Field type="text" name="companyName" id="companyName" placeholder="Your company name" />
                        </div>
                        <ErrorMessage name="companyName" component="div" className="field-error" />
                    </div>
                    <div className="form-group">
                        <label htmlFor="email">Email Address</label>
                        <div className="input-wrapper">
                            <FiMail className="input-icon" />
                            <Field type="email" name="email" id="email" placeholder="Your email address" />
                        </div>
                        <ErrorMessage name="email" component="div" className="field-error" />
                    </div>
                    <div className="form-actions">
                        <button type="submit" className="save-btn" disabled={isSubmitting || !dirty || loading}>
                           <FiSave/> {isSubmitting ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </Form>
            )}
        </Formik>
        </div>
    );
};

const SecurityForm = ({ handleSubmit }) => {
    const [showCurrentPassword, setShowCurrentPassword] = React.useState(false);
    const [showNewPassword, setShowNewPassword] = React.useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
    return (
    <div className="settings-section">
        <h3 className="section-title">Change Password</h3>
        <p className="section-subtitle">Update your existing password here.</p>
         <Formik
            initialValues={{ currentPassword: '', newPassword: '', confirmPassword: '' }}
            validationSchema={passwordValidationSchema}
            onSubmit={handleSubmit}
        >
            {({ isSubmitting }) => (
                <Form>
                    <div className="form-group">
                        <label htmlFor="currentPassword">Current Password</label>
                        <div className="password-input-wrapper">
                             <FiLock className="input-icon" />
                            <Field 
                              type={showCurrentPassword ? 'text' : 'password'} 
                              name="currentPassword" 
                              id="currentPassword" 
                              placeholder="Enter your current password"
                              className="password-input"
                              autoComplete="current-password"
                            />
                            <button
                              type="button"
                              className="toggle-password-btn"
                              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                              tabIndex={-1}
                              aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                            >
                              {showCurrentPassword ? <FiEyeOff /> : <FiEye />}
                            </button>
                        </div>
                        <ErrorMessage name="currentPassword" component="div" className="field-error" />
                    </div>
                     <div className="form-group">
                        <label htmlFor="newPassword">New Password</label>
                        <div className="password-input-wrapper">
                             <FiLock className="input-icon" />
                            <Field 
                              type={showNewPassword ? 'text' : 'password'} 
                              name="newPassword" 
                              id="newPassword" 
                              placeholder="Enter a new password"
                              className="password-input"
                              autoComplete="new-password"
                            />
                            <button
                              type="button"
                              className="toggle-password-btn"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              tabIndex={-1}
                              aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                            >
                              {showNewPassword ? <FiEyeOff /> : <FiEye />}
                            </button>
                        </div>
                        <ErrorMessage name="newPassword" component="div" className="field-error" />
                    </div>
                    <div className="form-group">
                        <label htmlFor="confirmPassword">Confirm New Password</label>
                        <div className="password-input-wrapper">
                            <FiLock className="input-icon" />
                            <Field 
                              type={showConfirmPassword ? 'text' : 'password'} 
                              name="confirmPassword" 
                              id="confirmPassword" 
                              placeholder="Confirm your new password"
                              className="password-input"
                              autoComplete="new-password"
                            />
                            <button
                              type="button"
                              className="toggle-password-btn"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              tabIndex={-1}
                              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                            >
                              {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                            </button>
                        </div>
                        <ErrorMessage name="confirmPassword" component="div" className="field-error" />
                    </div>
                    <div className="form-actions">
                         <button type="submit" className="save-btn" disabled={isSubmitting}>
                           <FiSave/> {isSubmitting ? 'Saving...' : 'Change Password'}
                        </button>
                    </div>
                </Form>
            )}
        </Formik>
    </div>
    );
};

const SetPasswordForm = ({ handleSubmit }) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
    
    return (
        <div className="settings-section">
            <div className="password-setup-header">
                <h3 className="section-title">Set Password</h3>
                <p className="section-subtitle">Create a secure password for your account.</p>
                <div className="password-warning-banner">
                    <FiLock />
                    <span>Setting a password allows you to login with email and password. Without it, you can only login with Google.</span>
                </div>
            </div>
            
            <Formik
                initialValues={{ password: '', confirmPassword: '' }}
                validationSchema={passwordSetupValidationSchema}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting, values, errors, touched }) => (
                    <Form>
                        <div className="form-group">
                            <label htmlFor="password">Password</label>
                            <div className="password-input-wrapper">
                                <FiLock className="input-icon" />
                                <Field 
                                    type={showPassword ? 'text' : 'password'} 
                                    name="password" 
                                    id="password" 
                                    placeholder="Enter your password"
                                    className="password-input"
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    className="toggle-password-btn"
                                    onClick={() => setShowPassword(!showPassword)}
                                    tabIndex={-1}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showPassword ? <FiEyeOff /> : <FiEye />}
                                </button>
                            </div>
                            <ErrorMessage name="password" component="div" className="field-error" />
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="confirmPassword">Confirm Password</label>
                            <div className="password-input-wrapper">
                                <FiLock className="input-icon" />
                                <Field 
                                    type={showConfirmPassword ? 'text' : 'password'} 
                                    name="confirmPassword" 
                                    id="confirmPassword" 
                                    placeholder="Confirm your password"
                                    className="password-input"
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    className="toggle-password-btn"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    tabIndex={-1}
                                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                                </button>
                            </div>
                            <ErrorMessage name="confirmPassword" component="div" className="field-error" />
                        </div>

                        <div className="password-requirements">
                            <h4>Password Requirements:</h4>
                            <ul>
                                <li className={values.password.length >= 8 ? 'valid' : ''}>
                                    At least 8 characters long
                                </li>
                                <li className={/(?=.*[a-z])/.test(values.password) ? 'valid' : ''}>
                                    One lowercase letter
                                </li>
                                <li className={/(?=.*[A-Z])/.test(values.password) ? 'valid' : ''}>
                                    One uppercase letter
                                </li>
                                <li className={/(?=.*\d)/.test(values.password) ? 'valid' : ''}>
                                    One number
                                </li>
                                <li className={/(?=.*[@$!%*?&])/.test(values.password) ? 'valid' : ''}>
                                    One special character (@$!%*?&)
                                </li>
                            </ul>
                        </div>
                        
                        <div className="form-actions">
                            <button type="submit" className="save-btn" disabled={isSubmitting || Object.keys(errors).length > 0}>
                                <FiSave/> {isSubmitting ? 'Setting Password...' : 'Set Password'}
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default Settings; 